import React, { useState, useRef, useEffect } from 'react';

import { Select } from 'antd';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getListFacility } from 'api/endpoints/auth';
import { changeFacility } from 'api/endpoints/user';
import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import useUser from 'hooks/useUser';
import { toastError, toastSuccess } from 'lib/toast';
import { setLoading } from 'store/actions/common';
import { convertToQueryString } from 'utils/helper';


const OPTION_PAGINATION = {
    page: 1,
    page_size: 1000,
};


const UpdateFacility = ({ back }) => {
    const { t } = useTranslation();
    const { me } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const { getMe } = useUser();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [listFacilities, setListFacilities] = useState([]);
    const isCallingApi = useRef(false);

    const initialValues = {
        facility_id: me?.facility_id,
    };

    useEffect(() => {
        // get list facility
        handleGetList();
    }, []);

    // get list data facility
    const handleGetList = async () => {
        if (!isCallingApi.current) {
            isCallingApi.current = true;
            const queryString = convertToQueryString({ page: OPTION_PAGINATION.page, page_size: OPTION_PAGINATION.page_size });
            setLoading(true);
            try {
                await getListFacility(queryString)
                    .then((response) => {
                        const data = response?.data.map((item) => {
                            return {
                                label: item?.name,
                                value: item?.id,
                            };
                        });

                        setListFacilities(data || []);
                    })
                    .catch(() => {
                    });
            } finally {
                isCallingApi.current = false;
            }
        }
    };

    const handleChangeFacility = async (values) => {
        try {
            dispatch(setLoading({ isOpen: true }));
            await changeFacility({ facility_id: values.facility_id })
                .then(async (response) => {
                    toastSuccess(response?.message || '');
                    getMe();
                    back();
                })
                .catch((errors) => {
                    toastError(errors.message);
                });
            dispatch(setLoading({ isOpen: false }));
        } catch {
            //  Handle error
            dispatch(setLoading({ isOpen: false }));
        }
    };

    const form = useFormik({
        initialValues: initialValues,
        validateOnChange: isSubmitted,
        validateOnBlur: false,
        validate: (values) => {
            const errors = {};

            if (!values.facility_id) errors.facility_id = true;

            setIsSubmitted(true);
            return errors;
        },
        onSubmit: async (values) => {
            await handleChangeFacility(values);
        },
    });

    return (
        <>
            <div className="flex-between-center px-6 py-4 border-b border-white-720">
                <p className="font-bold">{t('TITLE_FACILITY_EDITING')}</p>
            </div>

            <div className="p-6">
                <fieldset>
                    <p className="text-black/85 mb-2 flex items-center">
                        <span className="text-danger-411 mr-1">
                            <img src={require('assets/resources/Required.svg').default} alt="required" width={7} height={7} />
                        </span>
                        {t('FACILITY_NAME')}
                    </p>
                    <Select
                        value={form.values.facility_id || null}
                        showSearch
                        allowClear
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(value) => {
                            form.setFieldValue('facility_id', value);
                        }}
                        onClear={() => {
                            form.setFieldValue('facility_id', undefined);
                        }}
                        options={listFacilities}
                        className={(() => {
                            let _class = 'w-full h-10 focus:outline-none placeholder:text-base placeholder:text-secondary-354 border border-solid px-3 py-2 bg-white text-[16px]';
                            if (form.errors.facility_id) {
                                _class += ' border-danger-229';
                            } else {
                                _class += ' border-gray-651';
                            }

                            return _class;
                        })()}
                    />
                </fieldset>
                <div className='h-2'>
                    <p className="text-sm text-danger-229">{!form.values.facility_id && form.errors.facility_id && <ErrorMessage>{t('NOT_EMPTY')}</ErrorMessage>}</p>
                </div>
            </div>

            <div className="h-16 border-t border-white-720 flex-end-center gap-2 py-4 px-6">
                <Button className="h-8 w-25.5 text-sm" text={t('CANCEL_TEXT')} color="outline" onClick={back} />
                <Button className="h-8 w-20 text-sm" text={t('RENEW')} color="primary" onClick={form.handleSubmit} />
            </div>
        </>
    );
};

export default React.memo(UpdateFacility);
